import { useState } from "react";
import type { ComprehensionQuestion } from "../types/story";

/**
 * MOMENT 5 — comprehension as the story being curious about THEM.
 *
 * Not homework at the end: the coziest moment in the app. A small luminous story
 * friend turns to the child and wonders aloud; a tapped answer earns the
 * authored, in-character warmth (`affirmationCorrect` / `affirmationAny`) — never
 * a generic "Correct!" — and a right answer gets a tiny delighted bob and a few
 * sparkles (the chime + a single haptic fire from App). Right or not-quite, the
 * child is MET, not graded: there is no harsh "wrong" state, and a gentle "maybe
 * later" is always offered for bedtime fatigue.
 *
 * INTERACTIVE mode only. One question at a time; large tap targets.
 */
export function Comprehension({
  questions,
  onAnswered,
  onSkipped,
  onDone,
}: {
  questions: ComprehensionQuestion[];
  onAnswered: (qId: string, optionIndex: number, correct: boolean) => void;
  onSkipped: (qId: string) => void;
  onDone: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);

  const q = questions[index];
  const isLast = index >= questions.length - 1;

  if (!q) {
    onDone();
    return null;
  }

  const chosen = picked !== null ? q.options[picked] : null;
  const correct = !!chosen?.correct;
  const affirmation =
    correct && q.affirmationCorrect
      ? q.affirmationCorrect
      : q.affirmationAny ?? "What a lovely thing to think about.";
  const mood: FriendMood =
    picked === null ? "asking" : correct ? "delight" : "warm";

  function choose(i: number) {
    if (picked !== null) return; // one answer per question
    setPicked(i);
    onAnswered(q.id, i, !!q.options[i].correct);
  }

  function advance() {
    if (isLast) {
      onDone();
    } else {
      setIndex((n) => n + 1);
      setPicked(null);
    }
  }

  function skip() {
    onSkipped(q.id);
    advance();
  }

  return (
    <div className="comprehension cozy-quiz">
      <div className="friend-row">
        <StoryFriend mood={mood} />
        <QuizDots total={questions.length} index={index} />
      </div>

      <div className={`quiz-bubble quiz-bubble--${mood}`} aria-live="polite">
        {picked === null ? (
          <p className="q-prompt">{q.prompt}</p>
        ) : (
          <p className="q-affirm">{affirmation}</p>
        )}
        {correct && <Sparkles />}
      </div>

      <div className="q-options">
        {q.options.map((opt, i) => {
          const isPicked = picked === i;
          const cls = [
            "btn",
            "btn--choice",
            picked !== null && opt.correct ? "btn--correct" : "",
            isPicked && !opt.correct ? "btn--picked" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <button
              key={i}
              type="button"
              className={cls}
              onClick={() => choose(i)}
              disabled={picked !== null}
            >
              {opt.text}
            </button>
          );
        })}
      </div>

      {picked !== null ? (
        <button type="button" className="btn btn--next" onClick={advance}>
          {isLast ? "All done" : "Next"}
        </button>
      ) : (
        <button type="button" className="btn btn--ghost" onClick={skip}>
          Maybe later
        </button>
      )}
    </div>
  );
}

type FriendMood = "asking" | "delight" | "warm";

/**
 * A small luminous companion — the one who's curious about the child tonight.
 * It blinks now and then, sways gently while wondering, and its eyes go happy
 * crescents when an answer delights it — tiny procedural life, no assets.
 */
function StoryFriend({ mood }: { mood: FriendMood }) {
  return (
    <div className={`story-friend story-friend--${mood}`} aria-hidden="true">
      <span className="friend-aura" />
      <svg viewBox="0 0 64 64" className="friend-face">
        <circle cx="32" cy="32" r="21" className="friend-body" />
        {mood === "delight" ? (
          <>
            <path d="M21.5 30.5 Q25 26.6 28.5 30.5" className="friend-eye friend-eye--happy" />
            <path d="M35.5 30.5 Q39 26.6 42.5 30.5" className="friend-eye friend-eye--happy" />
          </>
        ) : (
          <>
            <circle cx="25" cy="30" r="2.6" className="friend-eye" />
            <circle cx="39" cy="30" r="2.6" className="friend-eye" />
          </>
        )}
        <path
          d={mood === "delight" ? "M23 37c3.5 5 14.5 5 18 0" : "M24 37c3 4 13 4 16 0"}
          className="friend-smile"
          fill="none"
          strokeWidth="2.4"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

/** A few sparkles that lift and fade — the delight of a right answer. */
function Sparkles() {
  return (
    <div className="quiz-sparks" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <span
          key={i}
          className="quiz-spark"
          style={{
            left: `${14 + i * 24}%`,
            animationDelay: `${i * 110}ms`,
          }}
        />
      ))}
    </div>
  );
}

/** Quiet progress for the grown-up — never a score for the child. */
function QuizDots({ total, index }: { total: number; index: number }) {
  return (
    <div className="quiz-dots" aria-hidden="true">
      {Array.from({ length: total }).map((_, i) => (
        <span key={i} className={i <= index ? "quiz-dot is-on" : "quiz-dot"} />
      ))}
    </div>
  );
}
